import { useNavigate } from 'react-router-dom'
import {
  Text,
  GreyGrey20,
  GreyGrey80,
  Primary13SemiBold,
  Primary13Regular,
  Primary14SemiBold,
  TextOverflow,
  SequenceMenu,
  SequenceMenuItemType,
  IconChevronDown,
  IconSettingsGear,
  IconBuildings,
  IconArrowBoxLeft,
  IconCheckmark,
  IconCode,
  IconLock,
  IconEyeSlash,
  IconMaintenance,
  Tooltip,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import { WorkspaceAvatar } from '../WorkspaceAvatar/WorkspaceAvatar'
import { Avatar } from '../Avatar/Avatar'
import { useNavigationV3Context } from 'components/NavigationV3/hooks/useNavigationV3Context'
import {
  isDemoEnv,
  isDevelopmentEnv,
  isLocalDevelopmentEnv,
  isProductionEnv,
  isSandboxEnv
} from 'lib/environment/environment'
import { usePermissions } from 'lib/rbac'
import { useAtom } from 'jotai'
import { screenshotModeAtom } from 'components/Layout/screenshotMode.atoms'
import { useDevToolsEnabled } from 'components/Layout/PerformanceMonitorBar'

interface MainMenuProps {
  isCollapsed?: boolean
}

export const MainMenu = ({ isCollapsed = false }: MainMenuProps) => {
  const { data, queries, functions } = useNavigationV3Context()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const [screenshotMode, setScreenshotMode] = useAtom(screenshotModeAtom)
  const [devToolsEnabled, setDevToolsEnabled] = useDevToolsEnabled()

  // The screenshot and dev tools items below are for us rather than our
  // customers, so they only show up where we work: the dev environment, and
  // your own machine. Neither is any use once the UI has already been stripped
  // back for a screenshot.
  const isDeveloperEnv = isDevelopmentEnv() || isLocalDevelopmentEnv()
  const hideDeveloperItems = !isDeveloperEnv || screenshotMode

  // At the moment we hide settings from users that can only read and can't invite
  const hideSettings = can('org.settings:read') && !can('org.settings:invite')

  const items: SequenceMenuItemType[] = [
    {
      label: 'Settings',
      iconLeft: <IconSettingsGear color="inherit" />,
      divider: true,
      onClick: () =>
        can('org.settings:update')
          ? navigate('/settings/merchant')
          : navigate('/settings/team'),
      hidden: !queries.canUseSettings() || hideSettings
    },
    {
      label: 'Switch workspace',
      iconLeft: <IconBuildings color="inherit" />,
      'data-testid': 'mainMenu.switchWorkspace',
      subItems: data.userWorkspaces.map(workspace => ({
        label: workspace.name,
        'data-testid': `mainMenu.switchWorkspace.${workspace.id}`,
        iconLeft: <Avatar name={workspace.name} />,
        iconRight:
          data.workspaceId === workspace.id ? (
            <IconCheckmark color="inherit" />
          ) : undefined,
        onClick: () => {
          void functions.switchWorkspace({ workspaceId: workspace.id })
        },
        style: {
          paddingTop: '7px !important',
          paddingBottom: '7px !important'
        }
      })),
      subItemsHeader: (
        <Flex padding="8px" gap="8px" flexDirection="column">
          <Text {...Primary13SemiBold} color={GreyGrey80}>
            Signed in with
          </Text>
          <Text {...Primary13Regular} color={GreyGrey80}>
            {data.userEmail}
          </Text>
        </Flex>
      ),
      hidden: !queries.canSwitchWorkspaces()
    },
    {
      label: 'Switch environment',
      iconLeft: <IconMaintenance color="inherit" />,
      divider: true,
      'data-testid': 'mainMenu.switchEnvironment',
      subItems: [
        {
          label: 'Sandbox',
          'data-testid': 'mainMenu.switchEnvironment.sandbox',
          onClick: () => {
            window.location.href = 'https://dashboard.sandbox.sequencehq.com'
          },
          iconRight: isSandboxEnv() ? (
            <IconCheckmark color="inherit" />
          ) : undefined
        },
        {
          label: 'Production',
          'data-testid': 'mainMenu.switchEnvironment.production',
          onClick: () => {
            window.location.href = 'https://dashboard.sequencehq.com'
          },
          iconRight: isProductionEnv() ? (
            <IconCheckmark color="inherit" />
          ) : undefined
        },
        {
          label: 'Demo mode',
          'data-testid': 'mainMenu.switchEnvironment.demo',
          onClick: () => {
            window.location.href = 'https://demo.sequencehq.com'
          },
          iconRight: isDemoEnv() ? <IconCheckmark color="inherit" /> : undefined
        }
      ]
    },
    {
      label: 'Request sandbox access',
      onClick: () => {
        window.location.href = 'https://access.sequencehq.com'
      },
      iconLeft: <IconLock color="inherit" />,
      hidden: !queries.canRequestSandboxAccess()
    },
    {
      label: 'Log out',
      iconLeft: <IconArrowBoxLeft color="inherit" />,
      onClick: () => {
        void functions.handleLogOut()
      },
      hidden: !queries.canLogOut()
    },
    {
      label: 'Hide UI for screenshots',
      iconLeft: <IconEyeSlash color="inherit" />,
      onClick: () => setScreenshotMode(true),
      hidden: hideDeveloperItems,
      'data-testid': 'mainMenu.hideUiForScreenshots'
    },
    {
      label: devToolsEnabled
        ? 'Hide Sequence dev tools'
        : 'Show Sequence dev tools',
      iconLeft: <IconCode />,
      onClick: () => setDevToolsEnabled(enabled => !enabled),
      hidden: hideDeveloperItems,
      'data-testid': 'mainMenu.devTools'
    }
  ]

  // Collapsed view with tooltip
  if (isCollapsed) {
    return (
      <Tooltip
        label={data.workspaceName}
        placement="right"
        hasArrow
        openDelay={300}
      >
        <SequenceMenu
          width={220}
          menuButton={
            <Grid
              as="button"
              data-testid="mainMenu"
              templateColumns="auto"
              padding="6px"
              borderRadius="6px"
              alignItems="center"
              justifyContent={'flex-start'}
              cursor="pointer"
              _hover={{ background: GreyGrey20 }}
              _focus={{ background: GreyGrey20 }}
            >
              <GridItem>
                <WorkspaceAvatar workspaceName={data.workspaceName} size="md" />
              </GridItem>
            </Grid>
          }
          items={items}
        />
      </Tooltip>
    )
  }

  // Default expanded view
  return (
    <SequenceMenu
      width={220}
      menuButton={
        <Grid
          as="button"
          data-testid="mainMenu"
          templateColumns="auto 1fr auto"
          padding="6px"
          gap="6px"
          borderRadius="6px"
          alignItems="center"
          justifyContent={'flex-start'}
          cursor="pointer"
          _hover={{ background: GreyGrey20 }}
          _focus={{ background: GreyGrey20 }}
        >
          <GridItem>
            <WorkspaceAvatar workspaceName={data.workspaceName} size="md" />
          </GridItem>
          <GridItem {...Primary14SemiBold} color={GreyGrey80} overflow="hidden">
            <TextOverflow splitPercentage={100}>
              {data.workspaceName}
            </TextOverflow>
          </GridItem>
          <GridItem>
            <IconChevronDown color="black" />
          </GridItem>
        </Grid>
      }
      items={items}
    />
  )
}
